import { EventEmitter } from '@angular/core';
import { LoadCasesFlowStep, LoadType, PartSubType, PartType } from '../../models/enums';
import { LoadItem } from '../../models/loadcase.model';
import {
  ArrowHelper,
  Box3,
  BufferAttribute,
  Color,
  DoubleSide,
  Mesh,
  Object3D,
  Raycaster,
  Vector2,
  Vector3,
  BufferGeometry,
  Group
} from 'three';
import { Line2 } from 'three/examples/jsm/lines/Line2.js';
import { LineGeometry } from 'three/examples/jsm/lines/LineGeometry.js';
import { Engine } from '../engine';
import { PerFaceMaterial, extractFaceGeometries } from './per-face-material';

export class Selector {
  public highlightedData: any = undefined;
  public highlightedFaces: any[] = [];
  public highlightedWires: any[] = [];
  public selectedFaces: string[] = [];
  public currentInteresected: any;
  public currentInteresectedData: any;
  public clicked = new EventEmitter();
  public moved = new EventEmitter();
  public intersectedEvent = new EventEmitter();
  public rectangleSelected = new EventEmitter<{
    objects: any[];
    pressedKeys: { ctrl: boolean; shift: boolean; alt: boolean };
  }>();
  public isMouseDown = false;
  public isMouseMoveHalted = false;
  public isMouseDownHalted = false;
  public currentPartForFaceSelection: any;
  public allowedFacesToSelect: string[] = [];
  public isFacesSelectionEnabled = false;
  public facesSelectionType?: LoadCasesFlowStep;
  public PartTypeEnum: typeof PartType = PartType;
  public notAllowedPartTypes: any[] = [];

  private raycaster = new Raycaster();

  // Selection rectangle state
  private dragStartPosition: { x: number; y: number } | null = null;
  private dragStartEvent: MouseEvent | null = null;
  private isDragging = false;
  private selectionRectElement: HTMLDivElement | null = null;
  private previewHighlightedObjects: any[] = [];
  private readonly DRAG_THRESHOLD = 5; // pixels

  public CADEditorEnabled = false;

  // Cursor management
  private currentCursor = 'default';

  constructor(
    public camera: any,
    public canvas: any,
    public groups: any,
    public group: any,
    public objects: any,
    public engine?: Engine
  ) {
    this.addEventListeners();
  }

  private addEventListeners() {
    this.canvas.addEventListener('mousedown', (e: any) => this.onMouseDown(e));
    this.canvas.addEventListener('mouseup', (e: any) => this.onMouseUp(e));
    this.canvas.addEventListener('mousemove', (e: MouseEvent) => this.onMouseMove(e));
    this.canvas.addEventListener('mouseleave', () => this.setCursor('default'));
  }

  private setCursor(cursor: string): void {
    if (this.currentCursor !== cursor) {
      this.currentCursor = cursor;
      this.canvas.style.cursor = cursor;
    }
  }

  private onMouseDown(e: MouseEvent) {
    if (this.CADEditorEnabled) return;
    if (e.altKey) return;

    this.isMouseDown = true;

    // Store drag start position for potential rectangle selection (only for part selection mode)
    if (e.button === 0 && !this.isFacesSelectionEnabled && this.rectangleSelected.observed) {
      this.dragStartPosition = { x: e.clientX, y: e.clientY };
      this.dragStartEvent = e;
      this.isDragging = false;
    }

    if (!this.clicked.observed || !this.currentInteresected || this.isMouseDownHalted) return;
    if (e.button !== 0) return;

    // For face selection, we need to get the faceId fresh from the intersection
    // because the intersection object may have been reused by three.js
    let faceId: string | null = null;

    if (this.isFacesSelectionEnabled && this.currentInteresected) {
      // Re-calculate faceId from the current intersection
      faceId = this.getFaceIdFromIntersection(this.currentInteresected);
    } else {
      // Fallback: try from attached property or userData
      faceId = this.currentInteresected.faceId || this.currentInteresected.object?.userData?.faceId || null;
    }

    // Don't emit click immediately - wait for mouseup to distinguish from drag
    // Store the click data for potential emission on mouseup
    if (!this.isFacesSelectionEnabled && this.rectangleSelected.observed) {
      // Will be handled in mouseup
      return;
    }

    this.clicked.emit({
      object: this.currentInteresected,
      data: this.currentInteresectedData,
      faceId: faceId, // Include faceId directly for easy access
      pressedKeys: {
        ctrl: e.ctrlKey,
        shift: e.shiftKey,
        alt: e.altKey
      }
    });
  }

  private onMouseUp(e: MouseEvent) {
    if (this.CADEditorEnabled) return;

    const wasDragging = this.isDragging;
    const startPos = this.dragStartPosition;
    const startEvent = this.dragStartEvent;

    // Reset state
    this.isMouseDown = false;
    this.isDragging = false;
    this.dragStartPosition = null;
    this.dragStartEvent = null;
    this.removeSelectionRect();

    // Reset cursor after drag
    if (wasDragging) {
      this.setCursor('default');
    }

    // Handle rectangle selection if was dragging
    if (wasDragging && startPos && this.rectangleSelected.observed) {
      const selectedObjects = this.getObjectsInScreenRect(
        startPos.x,
        startPos.y,
        e.clientX,
        e.clientY
      );

      if (selectedObjects.length > 0) {
        this.rectangleSelected.emit({
          objects: selectedObjects,
          pressedKeys: {
            ctrl: e.ctrlKey,
            shift: e.shiftKey,
            alt: e.altKey
          }
        });
      }
      return;
    }

    // If not dragging and we have a deferred click, emit it now
    if (!wasDragging && startEvent && this.currentInteresected && this.clicked.observed && !this.isMouseDownHalted) {
      const faceId = this.currentInteresected.faceId || this.currentInteresected.object?.userData?.faceId || null;
      this.clicked.emit({
        object: this.currentInteresected,
        data: this.currentInteresectedData,
        faceId: faceId,
        pressedKeys: {
          ctrl: e.ctrlKey,
          shift: e.shiftKey,
          alt: e.altKey
        }
      });
    }
  }

  private onMouseMove(e: MouseEvent) {
    if (this.CADEditorEnabled) return;

    // Handle selection rectangle dragging
    if (this.isMouseDown && this.dragStartPosition && !this.isFacesSelectionEnabled) {
      const dx = e.clientX - this.dragStartPosition.x;
      const dy = e.clientY - this.dragStartPosition.y;
      const distance = Math.sqrt(dx * dx + dy * dy);

      // Start dragging if moved beyond threshold
      if (distance > this.DRAG_THRESHOLD) {
        if (!this.isDragging) {
          this.isDragging = true;
          this.selectionRectElement = this.createSelectionRect();
          this.setCursor('crosshair');
        }
        this.updateSelectionRect(
          this.dragStartPosition.x,
          this.dragStartPosition.y,
          e.clientX,
          e.clientY
        );
      }
      return;
    }

    if (this.engine?.session.isViewOnlyPermission()) return;
    if (!this.clicked.observed || this.isMouseDown || this.isMouseMoveHalted) return;

    const rect = this.canvas.getBoundingClientRect();
    const mouseX = ((e.clientX - rect.x) / this.canvas.clientWidth) * 2 - 1;
    const mouseY = -((e.clientY - rect.y) / this.canvas.clientHeight) * 2 + 1;
    const mousePosition = new Vector2(mouseX, mouseY);

    this.raycaster.firstHitOnly = true;
    this.raycaster.setFromCamera(mousePosition, this.camera);

    const intersectsAllowedObjects = this.objects
      .filter((o: any) => this.notAllowedPartTypes.indexOf(o.type) === -1 && o.group.visible)
      .map((o: any) => o.group);

    const intersects = this.raycaster.intersectObjects(intersectsAllowedObjects) as any[];
    const intersected = intersects[0];

    if (!intersected || intersected.object?.name !== this.currentInteresected?.object?.name) {
      // Only unhighlight parts when NOT in face selection mode
      // Face highlighting uses different approach (color vs emissive)
      if (!this.isFacesSelectionEnabled) {
        this.unhighlightPart();
      }
      this.currentInteresected = null;
      this.currentInteresectedData = null;
      this.engine?.outlineEffect.selection.clear();
      this.engine?.addDrillBitsToOutline();

      // Reset cursor when not hovering over anything
      if (!intersected) {
        this.setCursor('default');
      }
    }

    // Always unhighlight faces when in face selection mode
    if (this.isFacesSelectionEnabled) {
      this.unhighlightFace();
    }

    if (intersected) {
      const data = this.objects.find((o: any) => o.data.id === intersected.object.userData?.['id']);
      if (data) {
        this.intersectedEvent.emit({ data, intersects });

        if (this.isFacesSelectionEnabled) {
          // Get the faceId from the intersection (works with both merged and legacy meshes)
          const faceId = this.getFaceIdFromIntersection(intersected);

          if (faceId && this.allowedFacesToSelect.indexOf(faceId) !== -1) {
            this.currentInteresected = intersected;
            this.currentInteresectedData = data;
            // Store the faceId in the intersection for later use
            intersected.faceId = faceId;
            this.highlightFace(data, intersected);
            this.setCursor('crosshair');
          } else {
            // Hovering over part but not a valid face
            this.setCursor('default');
          }
        } else {
          this.currentInteresected = intersected;
          this.currentInteresectedData = data;

          const obj = this.objects.find((o: any) => data.data.id === o.data.id);
          if (obj) {
            obj.group.children.forEach((child: any) => {
              if (child instanceof Mesh) {
                this.engine?.outlineEffect?.selection.add(child);
              }
            });
          }
          this.highlightPart(data);
          this.setCursor('pointer');
        }
      }
    }
  }

  /**
   * Get the faceId from an intersection with a merged mesh
   * Returns the faceId if it's a merged mesh, or the object's uuid for legacy meshes
   */
  private getFaceIdFromIntersection(intersect: any): string | null {
    const mesh = intersect.object;

    // Check if this is a merged mesh
    if (mesh?.userData?.isMergedMesh) {
      const faceIds = mesh.userData.faceIds as string[];
      const geometry = mesh.geometry;

      if (!geometry || !faceIds) return null;

      // Access faceIndex attribute using bracket notation
      const faceIndexAttr = geometry.attributes['faceIndex'] as BufferAttribute;
      if (!faceIndexAttr) return null;

      // Get the face from the intersection - three.js provides face.a, face.b, face.c as vertex indices
      const face = intersect.face;
      if (!face) return null;

      // Use one of the vertex indices directly (face.a is the first vertex of the hit triangle)
      const vertexIndex = face.a;

      // Get the faceIndex value from the vertex attribute
      const faceIndex = Math.round(faceIndexAttr.getX(vertexIndex));

      // Map to the original faceId
      if (faceIndex >= 0 && faceIndex < faceIds.length) {
        return faceIds[faceIndex];
      }

      return null;
    }

    // Legacy: return the mesh's faceId from userData if available, otherwise uuid
    if (mesh?.userData?.faceId) {
      return mesh.userData.faceId;
    }

    return mesh?.uuid || null;
  }

  public enableFaceSelection(obj: any | any[], step?: LoadCasesFlowStep) {
    this.currentPartForFaceSelection = obj;
    if (Array.isArray(obj)) {
      this.allowedFacesToSelect = obj.flatMap((o: any) => o.faces);
    } else {
      this.allowedFacesToSelect = obj.faces;
    }
    this.isFacesSelectionEnabled = true;
    if (step) {
      this.facesSelectionType = step;
    } else {
      this.facesSelectionType = undefined;
    }
  }

  public disableFaceSelection() {
    this.currentPartForFaceSelection = null;
    this.allowedFacesToSelect = [];
    this.isFacesSelectionEnabled = false;

    // Unhighlight any currently highlighted faces
    this.unhighlightFace();
  }

  /**
   * Get the merged mesh from a part's group, caching the result in userData
   */
  private getMergedMesh(group: any): { mesh: any; perFaceMaterial: PerFaceMaterial; originalColor: number } | null {
    if (!group) return null;

    // Check cache first
    if (group.userData._cachedMergedMesh !== undefined) {
      return group.userData._cachedMergedMesh;
    }

    // Find merged mesh
    const meshObj = group.children?.find((c: any) => c.userData?.isMergedMesh);

    if (meshObj?.userData?.perFaceMaterial) {
      const result = {
        mesh: meshObj,
        perFaceMaterial: meshObj.userData.perFaceMaterial as PerFaceMaterial,
        originalColor: meshObj.userData.originalColor
      };
      group.userData._cachedMergedMesh = result;
      return result;
    }

    group.userData._cachedMergedMesh = null;
    return null;
  }

  public restorePartsColorsToDesignAndNonDesign() {
    for (const obj of this.objects) {
      this.setPartColorByType(obj, obj.type);
    }
  }

  public setPartColorBySubType(data: { faces: string[]; wires: string[]; group?: any }, type: PartSubType) {
    const merged = this.getMergedMesh(data.group);

    if (merged) {
      // Use batched update for merged mesh
      const { perFaceMaterial, originalColor } = merged;
      const color =
        type === PartSubType.BOUNDARY_CONDITION ? 0xff4500 : type === PartSubType.LOAD ? 0x32cd32 : type === PartSubType.BASE ? 0x00bcd4 : originalColor;
      perFaceMaterial.setFacesColor(data.faces, color);
    } else {
      // Legacy: individual mesh objects per face
      for (const face of data.faces) {
        const obj = this.group.getObjectByName(face) as any;
        if (!obj?.material?.color) continue;

        switch (type) {
          case PartSubType.BOUNDARY_CONDITION:
            obj.material.color.set(0xff4500);
            break;
          case PartSubType.LOAD:
            obj.material.color.set(0x32cd32);
            break;
          case PartSubType.BASE:
            obj.material.color.set(0x00bcd4);
            break;
          default:
            obj.material.color.set(obj.userData?.originalColor || 0xffffff);
            break;
        }
      }
    }

    for (const wire of data.wires) {
      const obj = this.group.getObjectByName(wire) as any;
      if (!obj?.material) continue;

      if (obj.material.color) obj.material.color.set(0x000000);
      if (obj.material.linewidth !== undefined) obj.material.linewidth = 1;
    }
  }

  public setPartColorByType(data: { faces: string[]; wires: string[]; group?: any }, type: PartType) {
    this.highlightedData = undefined;
    this.highlightedFaces = [];
    this.highlightedWires = [];

    const designSettings = {
      transparent: true,
      opacity: 0.3,
      color: 0xff964f,
      side: DoubleSide,
      alphaHash: true,
      renderOrder: 1
    };

    const nonDesignSettings = {
      transparent: false,
      opacity: 1,
      color: 0xa7c7e7,
      alphaHash: false,
      renderOrder: 0
    };

    const resetSettings = {
      side: DoubleSide,
      transparent: false,
      opacity: 1,
      alphaHash: false,
      renderOrder: 0
    };

    const applySettings = (obj: any, settings: any) => {
      if (!obj?.material) return;
      if (obj.material.transparent !== undefined) obj.material.transparent = settings.transparent;
      if (obj.material.opacity !== undefined) obj.material.opacity = settings.opacity;
      if (obj.material.color) obj.material.color.set(settings.color);
      if (obj.material.side !== undefined) obj.material.side = settings.side ?? obj.material.side;
      if (obj.material.alphaHash !== undefined) obj.material.alphaHash = settings.alphaHash;
      obj.renderOrder = settings.renderOrder;
    };

    const merged = this.getMergedMesh(data.group);

    if (merged) {
      const { mesh, perFaceMaterial, originalColor } = merged;

      // Apply material-level settings for the entire mesh
      perFaceMaterial.transparent = type === PartType.DESIGN;
      perFaceMaterial.opacity = type === PartType.DESIGN ? 0.3 : 1;
      perFaceMaterial.alphaHash = type === PartType.DESIGN;
      mesh.renderOrder = type === PartType.DESIGN ? 1 : 0;

      // Set per-face colors using batched update
      const faceColor = type === PartType.DESIGN ? 0xff964f : type === PartType.NONDESIGN ? 0xa7c7e7 : originalColor;

      perFaceMaterial.beginBatchUpdate();
      for (const faceId of data.faces) {
        perFaceMaterial.setFaceState(faceId, faceColor, 0);
      }
      perFaceMaterial.endBatchUpdate();
    } else {
      // Legacy: individual mesh objects per face
      for (const face of data.faces) {
        const obj = this.group.getObjectByName(face) as any;
        if (!obj?.material) continue;

        if (obj.material.emissive) obj.material.emissive.set(0x000000);

        switch (type) {
          case PartType.DESIGN:
            applySettings(obj, designSettings);
            break;
          case PartType.NONDESIGN:
            applySettings(obj, nonDesignSettings);
            break;
          default:
            applySettings(obj, { ...resetSettings, color: obj.userData?.originalColor || 0xffffff });
            break;
        }
      }
    }

    for (const wire of data.wires) {
      const obj = this.group.getObjectByName(wire) as any;
      if (!obj?.material) continue;

      if (obj.material.color) obj.material.color.set(0x000000);
      if (obj.material.linewidth !== undefined) obj.material.linewidth = 1;
    }
  }

  public findObjectByUserData(obj: Object3D, key: string, value: any): Object3D | null {
    let foundObject: Object3D | null = null;

    obj.traverse((child) => {
      if (child.userData && child.userData[key] === value) {
        foundObject = child;
        return;
      }
    });

    return foundObject;
  }

  /**
   * Get geometries for specific face IDs from a part
   * Works with both merged meshes (new) and legacy individual face meshes
   */
  public getFaceGeometries(partData: any, faceIds: string[]): BufferGeometry[] {
    const merged = this.getMergedMesh(partData.group);

    if (merged && merged.mesh.geometry) {
      // Merged mesh: extract geometries from merged geometry
      const faceIdToIndex = merged.mesh.userData.faceIdToIndex as Map<string, number>;
      if (faceIdToIndex) {
        return extractFaceGeometries(merged.mesh.geometry, faceIdToIndex, faceIds);
      }
    }

    // Legacy: find individual face meshes
    const geometries: BufferGeometry[] = [];
    for (const faceId of faceIds) {
      const meshObj = this.findObjectByUserData(partData.group, 'faceId', faceId) as Mesh | null;
      if (meshObj && meshObj.geometry) {
        geometries.push(meshObj.geometry);
      }
    }
    return geometries;
  }

  public updateFaces(faceIds: string[]) {
    this.selectedFaces = [...faceIds];

    // Update faces for all objects
    for (const obj of this.objects) {
      const merged = this.getMergedMesh(obj.group);
      if (merged) {
        // Merged mesh: use PerFaceMaterial with batch update
        const matchingFaces = faceIds.filter((faceId) => obj.faces.includes(faceId));
        if (matchingFaces.length > 0) {
          merged.perFaceMaterial.setFacesColor(matchingFaces, 0xffd700);
        }
      } else {
        // Legacy: update individual mesh objects
        for (const faceId of faceIds) {
          if (!obj.faces.includes(faceId)) continue;
          const meshObject = this.findObjectByUserData(obj.group, 'faceId', faceId);
          if (meshObject && (meshObject as any).material?.color) {
            (meshObject as any).material.color.set(0xffd700);
          }
        }
      }
    }
  }

  public highlightFace(data: any, intersect: any) {
    // Preserve-pick mode: cursor still tracks the face (so click resolution
    // works) but no hover paint — only the click→yellow state is visible.
    // This keeps the manual preserve workflow free of stale fill colors
    // across the rest of the part.
    if (this.facesSelectionType === LoadCasesFlowStep.PRESERVES) {
      return;
    }

    const faceId = intersect.faceId;
    const merged = this.getMergedMesh(data.group);

    if (merged && faceId) {
      merged.perFaceMaterial.setFaceColor(faceId, 0x007bff);
      this.highlightedFaces.push(faceId);
      this.highlightedData = data;
    } else if (intersect.object?.uuid) {
      // Legacy: use the mesh object
      const obj = this.group.getObjectByName(intersect.object.uuid) as any;
      if (obj?.material?.color) {
        obj.material.color.set(0x007bff);
        this.highlightedFaces.push(intersect.object.uuid);
        this.highlightedData = data;
      }
    }
  }

  public unhighlightFace(force?: boolean) {
    // Preserve-pick mode never paints on hover (see highlightFace), so
    // there's nothing to restore here either. Just clear our internal
    // tracking and re-apply the selected-faces highlight so the click→
    // yellow state stays consistent.
    if (this.facesSelectionType === LoadCasesFlowStep.PRESERVES) {
      this.highlightedFaces = [];
      this.highlightedData = undefined;
      this.highlightedWires = [];
      this.updateFaces(this.selectedFaces);
      return;
    }

    // Store references before clearing to avoid race conditions
    const facesToUnhighlight = [...this.highlightedFaces];
    const dataToUse = this.highlightedData;

    if (facesToUnhighlight.length === 0 || !dataToUse) {
      return;
    }

    // Clear immediately to prevent race conditions with fast mouse movement
    this.highlightedFaces = [];
    this.highlightedData = undefined;
    this.highlightedWires = [];

    const merged = this.getMergedMesh(dataToUse.group);

    if (merged) {
      const { perFaceMaterial, originalColor } = merged;

      // Determine restore color based on context
      const restoreColor =
        this.engine?.session.project.projectType === 'soft_jaws'
          ? originalColor
          : this.facesSelectionType === LoadCasesFlowStep.LOADS
            ? 0x32cd32
            : 0xff4500;

      // Filter out selected faces and batch update
      const facesToRestore = facesToUnhighlight.filter((faceId) => !this.selectedFaces.includes(faceId));
      if (facesToRestore.length > 0) {
        perFaceMaterial.setFacesColor(facesToRestore, restoreColor);
      }
    } else {
      // Legacy: update individual mesh objects
      for (const face of facesToUnhighlight) {
        const obj = this.group.getObjectByName(face) as any;
        if (!obj?.material?.color) continue;

        const faceIndex = this.selectedFaces.indexOf(obj.userData?.faceId);
        if (faceIndex >= 0) continue;

        // For SoftJaws projects, restore original color
        if (this.engine?.session.project.projectType === 'soft_jaws') {
          obj.material.color.set(obj.userData?.originalColor || 0xffffff);
        } else {
          // For topology optimization, use the selection type colors
          if (this.facesSelectionType === LoadCasesFlowStep.LOADS) {
            obj.material.color.set(0x32cd32);
          } else {
            obj.material.color.set(0xff4500);
          }
        }
      }
    }

    this.updateFaces(this.selectedFaces);
  }

  public highlightPart(data: any) {
    if (data.type === PartType.DESIGN || data.type === PartType.NONDESIGN) return;

    const merged = this.getMergedMesh(data.group);

    if (merged) {
      // Use batched emissive update for merged mesh
      merged.perFaceMaterial.setFacesEmissive(data.faces, 1.0);
      this.highlightedFaces.push(...data.faces);
      this.highlightedData = data;
    } else {
      // Legacy: individual mesh objects per face
      for (const face of data.faces) {
        const obj = this.group.getObjectByName(face) as any;
        if (!obj?.material?.emissive) continue;
        obj.material.emissive.set(0x999999);
        this.highlightedFaces.push(face);
        this.highlightedData = data;
      }
    }

    for (const wire of data.wires) {
      const obj = this.group.getObjectByName(wire) as any;
      if (!obj?.material?.color) continue;
      obj.material.color.set(0x333333);
      this.highlightedWires.push(wire);
    }
  }

  public unhighlightPart() {
    // Store references before clearing to avoid race conditions
    const facesToUnhighlight = [...this.highlightedFaces];
    const wiresToUnhighlight = [...this.highlightedWires];
    const dataToUse = this.highlightedData;

    if (facesToUnhighlight.length === 0 || !dataToUse) {
      return;
    }

    // Clear immediately to prevent race conditions
    this.highlightedData = undefined;
    this.highlightedFaces = [];
    this.highlightedWires = [];

    const merged = this.getMergedMesh(dataToUse.group);

    if (merged) {
      // Use batched emissive reset for merged mesh
      merged.perFaceMaterial.setFacesEmissive(facesToUnhighlight, 0);
    } else {
      // Legacy: individual mesh objects per face
      for (const face of facesToUnhighlight) {
        const obj = this.group.getObjectByName(face) as any;
        if (obj?.material?.emissive) obj.material.emissive.set(0x000000);
      }
    }

    for (const wire of wiresToUnhighlight) {
      const obj = this.group.getObjectByName(wire) as any;
      if (obj?.material?.color) obj.material.color.set(0x000000);
    }
  }

  public getPartType(item: any): PartType {
    return item.type;
  }

  public setPartType(item: any, type: PartType) {
    item.type = type;
    this.setPartColorByType(item, type);
  }

  // These annotation methods are no-ops - annotations are currently disabled
  public showLoadAnnotation(_group: Group) {}
  public hideLoadAnnotation(_group: Group) {}
  public showBoundaryConditionAnnotation(_group: Group) {}
  public hideBoundaryConditionAnnotation(_group: Group) {}

  public showForceAndMomentWithManipulator(group: Group) {
    const { partArrow, partMoment, partSphere, partLink } = this.getForceAndMomentWithManipulator(group);
    partArrow?.children.forEach((o) => o.layers.set(0));
    partMoment?.children.forEach((o) => o.layers.set(0));
    partSphere?.layers.set(0);
    partLink?.layers.set(0);
  }

  public setForceAndMomentWithManipulator(group: Group, load: LoadItem) {
    const { partArrow, partMoment, partSphere, partLink } = this.getForceAndMomentWithManipulator(group);
    const remotePoint = new Vector3(...load.remotePoint);
    const forceComponent = new Vector3(...load.forceComponent);
    const momentComponent = new Vector3(...load.momentComponent);

    partArrow?.position.copy(remotePoint);
    partMoment?.position.copy(remotePoint);
    partSphere?.position.copy(remotePoint);
    const geometry = new LineGeometry();
    const center = new Box3().setFromObject(group).getCenter(new Vector3());
    center.applyMatrix4(group.matrixWorld.clone().invert());
    group.localToWorld(center);
    geometry.setPositions([center.x, center.y, center.z, remotePoint.x, remotePoint.y, remotePoint.z]);
    partLink?.geometry.copy(geometry); //.setFromPoints([group.position.clone().addScalar(100), remotePoint]);
    partLink?.computeLineDistances();
    partLink?.scale.set(1, 1, 1);

    partArrow?.setDirection(forceComponent);
    partMoment?.setDirection(momentComponent);

    partArrow?.children.forEach((o) => o.layers.set(forceComponent.equals(new Vector3(0, 0, 0)) ? 1 : 0));
    partMoment?.children.forEach((o) => o.layers.set(momentComponent.equals(new Vector3(0, 0, 0)) ? 1 : 0));

    if (load.type === LoadType.ACCELERATION) {
      partSphere?.layers.set(1);
      partArrow?.children.forEach((o) => o.layers.set(1));
      partMoment?.children.forEach((o) => o.layers.set(1));
    }
  }

  public dragForceAndMomentWithManipulator(group: Group, enabled: boolean) {
    const { partSphere } = this.getForceAndMomentWithManipulator(group);
    if (partSphere?.userData['dragControl']) {
      partSphere.userData['dragControl'].enabled = enabled;
    }
  }

  public hideForceAndMomentArrow(group?: Group) {
    if (group) {
      const { partArrow, partMoment } = this.getForceAndMomentWithManipulator(group);
      partArrow?.children.forEach((o) => o.layers.set(1));
      partMoment?.children.forEach((o) => o.layers.set(1));
    }
  }

  public hideForceAndMomentWithManipulator(group?: Group) {
    if (group) {
      const { partArrow, partMoment, partSphere, partLink } = this.getForceAndMomentWithManipulator(group);
      partArrow?.children.forEach((o) => o.layers.set(1));
      partMoment?.children.forEach((o) => o.layers.set(1));
      partSphere?.layers.set(1);
      partLink?.layers.set(1);
    } else {
      for (const obj of this.objects) {
        this.hideForceAndMomentWithManipulator(obj.group);
      }
    }
  }

  public getForceAndMomentWithManipulator(group: Group) {
    const partArrow = group.userData['part_arrow'] as ArrowHelper;
    const partMoment = group.userData['part_moment'] as ArrowHelper;
    const partSphere = group.userData['part_sphere'];
    const partLink = group.userData['part_link_line'] as Line2;
    return { partArrow, partMoment, partSphere, partLink };
  }

  // ============ Selection Rectangle Methods ============

  private createSelectionRect(): HTMLDivElement {
    const rect = document.createElement('div');
    rect.style.position = 'fixed';
    rect.style.pointerEvents = 'none';
    rect.style.zIndex = '10000';
    rect.style.border = '2px solid rgba(160, 255, 0, 0.8)'; // hero-green
    rect.style.backgroundColor = 'rgba(160, 255, 0, 0.1)'; // hero-green with subtle opacity

    document.body.appendChild(rect);
    return rect;
  }

  private updateSelectionRect(startX: number, startY: number, currentX: number, currentY: number): void {
    if (!this.selectionRectElement) return;

    const left = Math.min(startX, currentX);
    const top = Math.min(startY, currentY);
    const width = Math.abs(currentX - startX);
    const height = Math.abs(currentY - startY);

    this.selectionRectElement.style.left = `${left}px`;
    this.selectionRectElement.style.top = `${top}px`;
    this.selectionRectElement.style.width = `${width}px`;
    this.selectionRectElement.style.height = `${height}px`;

    // Update preview highlights to show which objects will be selected
    const objectsInRect = this.getObjectsInScreenRect(startX, startY, currentX, currentY);
    this.updatePreviewHighlights(objectsInRect);
  }

  private removeSelectionRect(): void {
    // Clear preview highlights
    this.clearPreviewHighlights();

    if (this.selectionRectElement) {
      this.selectionRectElement.remove();
      this.selectionRectElement = null;
    }
  }

  private updatePreviewHighlights(objectsInRect: any[]): void {
    // Find objects to unhighlight (were highlighted but no longer in rect)
    const objectsToUnhighlight = this.previewHighlightedObjects.filter(
      (obj) => !objectsInRect.includes(obj)
    );

    // Find objects to highlight (in rect but not yet highlighted)
    const objectsToHighlight = objectsInRect.filter(
      (obj) => !this.previewHighlightedObjects.includes(obj)
    );

    // Unhighlight objects no longer in selection
    for (const obj of objectsToUnhighlight) {
      this.unhighlightPreviewObject(obj);
    }

    // Highlight new objects in selection
    for (const obj of objectsToHighlight) {
      this.highlightPreviewObject(obj);
    }

    // Update tracked list
    this.previewHighlightedObjects = [...objectsInRect];
  }

  private highlightPreviewObject(obj: any): void {
    if (!obj?.group) return;

    // Add to outline effect for visual feedback
    obj.group.children.forEach((child: any) => {
      if (child instanceof Mesh) {
        this.engine?.outlineEffect?.selection.add(child);
      }
    });

    // Also highlight using emissive for merged meshes
    const merged = this.getMergedMesh(obj.group);
    if (merged) {
      merged.perFaceMaterial.setFacesEmissive(obj.faces, 0.5);
    }
  }

  private unhighlightPreviewObject(obj: any): void {
    if (!obj?.group) return;

    // Remove from outline effect
    obj.group.children.forEach((child: any) => {
      if (child instanceof Mesh) {
        this.engine?.outlineEffect?.selection.delete(child);
      }
    });

    // Reset emissive for merged meshes
    const merged = this.getMergedMesh(obj.group);
    if (merged) {
      merged.perFaceMaterial.setFacesEmissive(obj.faces, 0);
    }
  }

  private clearPreviewHighlights(): void {
    for (const obj of this.previewHighlightedObjects) {
      this.unhighlightPreviewObject(obj);
    }
    this.previewHighlightedObjects = [];
  }

  private getObjectsInScreenRect(x1: number, y1: number, x2: number, y2: number): any[] {
    const rect = this.canvas.getBoundingClientRect();
    const minX = Math.min(x1, x2);
    const maxX = Math.max(x1, x2);
    const minY = Math.min(y1, y2);
    const maxY = Math.max(y1, y2);

    const selectedObjects: any[] = [];

    for (const obj of this.objects) {
      // Skip not allowed part types and invisible objects
      if (this.notAllowedPartTypes.indexOf(obj.type) !== -1 || !obj.group.visible) {
        continue;
      }

      // Get the bounding box center of the object
      const box = new Box3().setFromObject(obj.group);
      const center = box.getCenter(new Vector3());

      // Project 3D point to screen coordinates
      const screenPos = center.clone().project(this.camera);

      // Convert from NDC (-1 to 1) to screen pixels
      const screenX = ((screenPos.x + 1) / 2) * this.canvas.clientWidth + rect.x;
      const screenY = ((-screenPos.y + 1) / 2) * this.canvas.clientHeight + rect.y;

      // Check if the screen position is within the selection rectangle
      if (screenX >= minX && screenX <= maxX && screenY >= minY && screenY <= maxY) {
        selectedObjects.push(obj);
      }
    }

    return selectedObjects;
  }
}
